import React, { useState } from 'react';
import { supabase } from '../../services/supabase';
import QRCode from 'qrcode';

const TableManagement = ({ restaurant, tables, setTables, onDataChange }) => {
  // Modal states
  const [showAddTable, setShowAddTable] = useState(false);
  const [showEditTable, setShowEditTable] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [generatingQR, setGeneratingQR] = useState({});

  // Form states
  const [tableForm, setTableForm] = useState({ customName: '' });
  const [tableErrors, setTableErrors] = useState({});

  const validateTableForm = () => {
    const errors = {};
    if (!tableForm.customName.trim()) errors.customName = 'Table name is required';
    if (tableForm.customName.trim().length > 50) errors.customName = 'Table name must be less than 50 characters';
    return errors;
  };

  // Table Management with Custom Names
  const handleAddTable = async () => {
    const errors = validateTableForm();
    if (Object.keys(errors).length > 0) {
      setTableErrors(errors);
      return;
    }

    try {
      const nextTableNumber = tables.length > 0 ? Math.max(...tables.map(t => t.table_number)) + 1 : 1;
      const qrCode = `TABLE-${restaurant.slug}-${nextTableNumber}-${Date.now()}`;

      const { data: newTable, error } = await supabase
        .from('restaurant_tables')
        .insert({
          restaurant_id: restaurant.id,
          table_number: nextTableNumber,
          custom_name: tableForm.customName.trim(),
          qr_code: qrCode,
          is_active: true
        })
        .select()
        .single();

      if (error) throw error;

      setTableForm({ customName: '' });
      setTableErrors({});
      setShowAddTable(false);
      if (newTable) {
        setTables(prev => [...prev, newTable]);
        window.menuronDesktop?.local?.upsertTable(newTable);
      } else {
        onDataChange();
      }
    } catch (error) {
      console.error('Error adding table:', error);
      alert('Failed to add table');
    }
  };

  const handleEditTable = (table) => {
    setEditingTable(table);
    setTableForm({ customName: table.custom_name || `Table ${table.table_number}` });
    setTableErrors({});
    setShowEditTable(true);
  };

  const handleUpdateTable = async () => {
    const errors = validateTableForm();
    if (Object.keys(errors).length > 0) {
      setTableErrors(errors);
      return;
    }

    try {
      const { error } = await supabase
        .from('restaurant_tables')
        .update({
          custom_name: tableForm.customName.trim()
        })
        .eq('id', editingTable.id);

      if (error) throw error;

      setTableForm({ customName: '' });
      setTableErrors({});
      setShowEditTable(false);
      setEditingTable(null);
      onDataChange();
    } catch (error) {
      console.error('Error updating table:', error);
      alert('Failed to update table name');
    }
  };

  const handleDeleteTable = async (tableId, tableName) => {
    if (!window.confirm(`Are you sure you want to delete "${tableName}"?`)) return;

    try {
      const { error } = await supabase
        .from('restaurant_tables')
        .delete()
        .eq('id', tableId);

      if (error) throw error;
      setTables(prev => prev.filter(t => t.id !== tableId));
      window.menuronDesktop?.local?.removeTable(tableId);
    } catch (error) {
      console.error('Error deleting table:', error);
      alert('Failed to delete table');
    }
  };

  // Enhanced QR Code Management with Custom Branding
  const generateOrViewQRCode = async (table) => {

    setGeneratingQR(prev => ({ ...prev, [table.id]: true }));

    try {
      // In Electron, window.location.origin is file:// or localhost — always use prod URL
      const baseUrl = window.menuronDesktop?.isDesktop
        ? 'https://menuron.prismatic-a.com'
        : window.location.origin
      const qrUrl = `${baseUrl}/menu/${table.qr_code}`;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Set canvas size
      canvas.width = 400;
      canvas.height = 520;

      // Fill white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Add gradient header background
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, 90);
      gradient.addColorStop(0, '#FF4444');
      gradient.addColorStop(1, '#FF6B00');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, 90);

      // Function to wrap text
      const wrapText = (text, maxWidth) => {
        const words = text.split(' ');
        const lines = [];
        let currentLine = '';

        ctx.font = `bold ${fontSize}px Arial, sans-serif`;

        for (const word of words) {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          const testWidth = ctx.measureText(testLine).width;

          if (testWidth > maxWidth && currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        }
        if (currentLine) lines.push(currentLine);
        return lines;
      };

      // Restaurant name with dynamic sizing
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';

      const maxNameWidth = canvas.width - 40;
      let fontSize = 22;
      ctx.font = `bold ${fontSize}px Arial, sans-serif`;

      // Reduce font size if single line is too wide
      while (ctx.measureText(restaurant.name).width > maxNameWidth && fontSize > 14) {
        fontSize -= 2;
        ctx.font = `bold ${fontSize}px Arial, sans-serif`;
      }

      // Wrap text with maximum 2 lines
      let nameLines = wrapText(restaurant.name, maxNameWidth);

      if (nameLines.length > 2) {
        nameLines = nameLines.slice(0, 2);
        let lastLine = nameLines[1];
        ctx.font = `bold ${fontSize}px Arial, sans-serif`;
        while (ctx.measureText(lastLine + '...').width > maxNameWidth && lastLine.length > 0) {
          lastLine = lastLine.slice(0, -1);
        }
        nameLines[1] = lastLine.trim() + '...';
      }

      // Draw restaurant name lines
      const lineHeight = fontSize + 4;
      const headerCenterY = 45;
      const totalTextHeight = nameLines.length * lineHeight;
      const startY = headerCenterY - (totalTextHeight / 2) + (fontSize / 2);

      nameLines.forEach((line, index) => {
        ctx.fillText(line, canvas.width / 2, startY + (index * lineHeight));
      });

      // Add table name
      ctx.font = 'bold 16px Arial, sans-serif';
      const tableName = table.custom_name || `Table ${table.table_number}`;
      ctx.fillText(tableName, canvas.width / 2, 82);

      // Generate QR code
      const qrCodeDataUrl = await QRCode.toDataURL(qrUrl, {
        width: 280,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });

      const qrImage = new Image();
      await new Promise((resolve) => {
        qrImage.onload = resolve;
        qrImage.src = qrCodeDataUrl;
      });

      // Draw QR code at fixed position
      ctx.drawImage(qrImage, 60, 110, 280, 280);

      // Bottom text
      ctx.fillStyle = '#666666';
      ctx.font = '14px Arial, sans-serif';
      ctx.fillText('Provided by Menuron', canvas.width / 2, 420);

      ctx.fillStyle = '#333333';
      ctx.font = 'bold 16px Arial, sans-serif';
      ctx.fillText('Scan to view menu', canvas.width / 2, 445);

      const customQRImageUrl = canvas.toDataURL('image/png');

      const { error } = await supabase
        .from('restaurant_tables')
        .update({
          qr_image_url: customQRImageUrl,
          qr_generated_at: new Date().toISOString()
        })
        .eq('id', table.id);

      if (error) throw error;

      setTables(prev => prev.map(t =>
        t.id === table.id
          ? { ...t, qr_image_url: customQRImageUrl, qr_generated_at: new Date().toISOString() }
          : t
      ));

      setSelectedTable({ ...table, qr_image_url: customQRImageUrl });
      setShowQRModal(true);

    } catch (error) {
      console.error('Error generating QR code:', error);
      alert('Failed to generate QR code. Please try again.');
    } finally {
      setGeneratingQR(prev => ({ ...prev, [table.id]: false }));
    }
  };

  const downloadQRCode = () => {
    if (!selectedTable.qr_image_url) return;

    const link = document.createElement('a');
    link.href = selectedTable.qr_image_url;
    link.download = `${selectedTable.custom_name || 'Table-' + selectedTable.table_number}-qr.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper function to get button text and style
  const getQRButtonConfig = (table) => {
    if (generatingQR[table.id]) {
      return {
        text: 'Generating...',
        style: { backgroundColor: '#6c757d', cursor: 'not-allowed' },
        disabled: true
      };
    }

    if (table.qr_code) {
      return {
        text: 'View QR Code',
        style: { backgroundColor: '#17a2b8' },
        disabled: false
      };
    }

    return {
      text: 'Generate QR',
      style: { backgroundColor: '#28a745' },
      disabled: false
    };
  };

  return (
    <>
      {/* Table Management with Custom Names */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '20px',
        padding: '25px',
        boxShadow: '0 15px 40px rgba(0,0,0,0.1)',
        border: '2px solid rgba(255, 68, 68, 0.1)',
        height: 'fit-content'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '25px',
          paddingBottom: '15px',
          borderBottom: '2px solid #f8f9fa'
        }}>
          <h2 style={{
            margin: '0',
            color: '#333',
            fontSize: '1.5rem',
            fontWeight: '700'
          }}>
            Table Management
          </h2>
          <button
            onClick={() => setShowAddTable(true)}
            style={{
              background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '10px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(255, 68, 68, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-1px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            + Add Table
          </button>
        </div>

        <div style={{ display: 'grid', gap: '15px' }}>
          {tables.map(table => {
            const buttonConfig = getQRButtonConfig(table);

            return (
              <div key={table.id} style={{
                border: '1px solid #e9ecef',
                borderRadius: '12px',
                padding: '15px',
                backgroundColor: '#fefefe',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.borderColor = '#FF6B00'}
              onMouseOut={(e) => e.currentTarget.style.borderColor = '#e9ecef'}>
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '5px' }}>
                    <h3 style={{
                      margin: '0',
                      fontSize: '1.1rem',
                      color: '#333',
                      fontWeight: '700'
                    }}>
                      {table.custom_name || `Table ${table.table_number}`}
                    </h3>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        onClick={() => handleEditTable(table)}
                        style={{
                          backgroundColor: 'transparent',
                          color: '#007bff',
                          border: '1px solid #007bff',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.7rem',
                          fontWeight: '600'
                        }}
                        title="Edit table name"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteTable(table.id, table.custom_name || `Table ${table.table_number}`)}
                        style={{
                          backgroundColor: 'transparent',
                          color: '#dc3545',
                          border: '1px solid #dc3545',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.7rem',
                          fontWeight: '600'
                        }}
                        title="Delete table"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  <p style={{
                    margin: '0',
                    fontSize: '0.8rem',
                    color: '#666',
                    wordBreak: 'break-all'
                  }}>
                    QR Code: {table.qr_code}
                  </p>
                  {table.qr_generated_at && (
                    <p style={{
                      margin: '5px 0 0 0',
                      fontSize: '0.75rem',
                      color: '#28a745',
                      fontWeight: '500'
                    }}>
                      QR Generated: {new Date(table.qr_generated_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => generateOrViewQRCode(table)}
                  disabled={buttonConfig.disabled}
                  style={{
                    color: 'white',
                    border: 'none',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    cursor: buttonConfig.disabled ? 'not-allowed' : 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    width: '100%',
                    transition: 'all 0.3s ease',
                    ...buttonConfig.style
                  }}
                >
                  {buttonConfig.text}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Table Modal with Custom Name */}
      {showAddTable && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: '1000'
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '15px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '1.4rem' }}>Add New Table</h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                Table Name *
              </label>
              <input
                type="text"
                placeholder="Enter table name"
                value={tableForm.customName}
                onChange={(e) => {
                  setTableForm({ customName: e.target.value });
                  if (tableErrors.customName) setTableErrors({ ...tableErrors, customName: '' });
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: tableErrors.customName ? '2px solid #dc3545' : '2px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
                autoFocus
              />
              {tableErrors.customName && (
                <div style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '5px' }}>
                  {tableErrors.customName}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAddTable(false);
                  setTableForm({ customName: '' });
                  setTableErrors({});
                }}
                style={{
                  padding: '12px 24px',
                  border: '2px solid #6c757d',
                  backgroundColor: 'transparent',
                  color: '#6c757d',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddTable}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#FF6B00',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Add Table
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Table Modal */}
      {showEditTable && editingTable && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: '1000'
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '15px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '1.4rem' }}>
              Edit Table Name
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                Table Name *
              </label>
              <input
                type="text"
                placeholder="Enter table name"
                value={tableForm.customName}
                onChange={(e) => {
                  setTableForm({ customName: e.target.value });
                  if (tableErrors.customName) setTableErrors({ ...tableErrors, customName: '' });
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: tableErrors.customName ? '2px solid #dc3545' : '2px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
                autoFocus
              />
              {tableErrors.customName && (
                <div style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '5px' }}>
                  {tableErrors.customName}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowEditTable(false);
                  setEditingTable(null);
                  setTableForm({ customName: '' });
                  setTableErrors({});
                }}
                style={{
                  padding: '12px 24px',
                  border: '2px solid #6c757d',
                  backgroundColor: 'transparent',
                  color: '#6c757d',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateTable}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Update Name
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced QR Code Modal with Custom Name */}
      {showQRModal && selectedTable && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: '1000'
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '15px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: '0', color: '#333', fontSize: '1.2rem' }}>
                QR Code - {selectedTable.custom_name || `Table ${selectedTable.table_number}`}
              </h3>
              <button
                onClick={() => setShowQRModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#999'
                }}
              >
                ×
              </button>
            </div>

            <div style={{
              background: '#f8f9fa',
              padding: '20px',
              borderRadius: '10px',
              border: '2px solid #e9ecef',
              marginBottom: '20px'
            }}>
              <img
                src={selectedTable.qr_image_url}
                alt={`QR Code for ${selectedTable.custom_name || `Table ${selectedTable.table_number}`}`}
                style={{ width: '200px', height: '200px' }}
              />
            </div>

            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '10px' }}>
              Customers scan this code to access the menu for {selectedTable.custom_name || `Table ${selectedTable.table_number}`}
            </p>

            {selectedTable.qr_generated_at && (
              <p style={{ fontSize: '0.8rem', color: '#28a745', marginBottom: '20px' }}>
                Generated: {new Date(selectedTable.qr_generated_at).toLocaleString()}
              </p>
            )}

            <button
              onClick={downloadQRCode}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Download QR Code
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default TableManagement;
